import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../../Api/config";
import { MdOutlineContactMail, MdDelete } from "react-icons/md";

const Inquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/contacts");
      setInquiries(response.data || []);
    } catch (err) {
      console.error("Error fetching inquiries:", err);
      setError("Failed to fetch contact inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const res = await axiosInstance.post(`/contacts/status/${id}`, {
        status: newStatus,
      });
      if (res.data.success) {
        setSuccessMsg(`Inquiry status updated to ${newStatus}!`);
        fetchInquiries();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to update status.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry record?")) return;
    try {
      const res = await axiosInstance.delete(`/contacts/${id}`);
      if (res.data.success) {
        setSuccessMsg("Inquiry record deleted.");
        fetchInquiries();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to delete record.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-600">Admin</p>
        <h1 className="mt-2 text-3xl font-bold text-slate-950">Contact Inquiries</h1>
      </div>

      {successMsg && (
        <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg text-sm font-semibold">
          {successMsg}
        </div>
      )}

      {error && (
        <div className="bg-rose-50 text-rose-600 border border-rose-100 px-4 py-3 rounded-lg text-sm font-medium">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      ) : inquiries.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 shadow-sm">
          <MdOutlineContactMail className="text-5xl mx-auto text-slate-300 mb-3" />
          <p>No contact inquiries received yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="p-4">Sender Details</th>
                  <th className="p-4">Model Interest</th>
                  <th className="p-4">Message</th>
                  <th className="p-4">Submitted At</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-150">
                {inquiries.map((inq) => (
                  <tr key={inq.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4">
                      <div className="font-semibold text-slate-900">{inq.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{inq.phone}</div>
                    </td>
                    <td className="p-4 font-semibold text-slate-800">
                      {inq.bike || <span className="text-slate-400 font-normal">Just Enquiring</span>}
                    </td>
                    <td className="p-4 text-slate-650 max-w-sm whitespace-pre-wrap leading-relaxed">
                      {inq.message || <span className="text-slate-350 italic">No message details</span>}
                    </td>
                    <td className="p-4 text-slate-500 font-medium font-sans">
                      {new Date(inq.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="p-4">
                      <select
                        value={inq.status}
                        onChange={(e) => handleUpdateStatus(inq.id, e.target.value)}
                        className={`text-xs font-bold rounded-lg px-2.5 py-1.5 border outline-none cursor-pointer transition ${
                          inq.status === "new"
                            ? "bg-blue-50 text-blue-700 border-blue-200"
                            : inq.status === "contacted"
                            ? "bg-amber-50 text-amber-700 border-amber-250"
                            : "bg-emerald-50 text-emerald-750 border-emerald-200"
                        }`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleDelete(inq.id)}
                        className="p-2 text-slate-550 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        title="Delete"
                      >
                        <MdDelete className="text-lg" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inquiries;
